import { useState, useEffect } from 'react';
import api from '../api';
import { Trophy, Plus, Pencil, Trash2, X, CheckCircle, Circle, Crown, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { WsopLeaderboardReport, WptMonthlyReport } from '../components/LeaderboardReports';

const GAMES = ['poker', 'cal_games'];
const EMPTY = {
  slug: '', name: '', subtitle: '', startDate: '', endDate: '',
  qualifyCount: 9, playoffEndRank: '', enabled: true,
  scoring: { basePerHour: 50, games: ['poker'], timeMultipliers: [
    { startHour: 0, endHour: 6, multiplier: 3 },
    { startHour: 6, endHour: 18, multiplier: 5 },
    { startHour: 18, endHour: 24, multiplier: 1 },
  ] },
  exemptUserIds: [],
};

export default function Leaderboards() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('manage'); // manage | wsop | wpt
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Load the leaderboard report data when a report tab is selected.
  useEffect(() => {
    if (view === 'manage') return;
    let active = true;
    setReportLoading(true);
    setReportData(null);
    const url = view === 'wsop' ? '/admin/reports/wsop-leaderboard' : '/admin/reports/wpt-monthly?topN=100';
    api.get(url)
      .then((r) => { if (active) setReportData(r.data); })
      .catch(() => { if (active) toast.error('Failed to load leaderboard report'); })
      .finally(() => { if (active) setReportLoading(false); });
    return () => { active = false; };
  }, [view]);

  const fetchList = async () => {
    try {
      const res = await api.get('/admin/leaderboards');
      setList(res.data.leaderboards || []);
    } catch (e) { toast.error('Failed to load leaderboards'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, []);

  const openCreate = () => { setEditing(null); setForm(JSON.parse(JSON.stringify(EMPTY))); setShowModal(true); };
  const openEdit = (lb) => {
    setEditing(lb);
    setForm({
      slug: lb.slug, name: lb.name, subtitle: lb.subtitle || '',
      startDate: lb.startDate || '', endDate: lb.endDate || '',
      qualifyCount: lb.qualifyCount ?? 9, playoffEndRank: lb.playoffEndRank ?? '',
      enabled: lb.enabled !== false,
      scoring: {
        basePerHour: lb.scoring?.basePerHour ?? 50,
        games: lb.scoring?.games || ['poker'],
        timeMultipliers: lb.scoring?.timeMultipliers || EMPTY.scoring.timeMultipliers,
      },
      exemptUserIds: lb.exemptUserIds || [],
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.slug.trim()) { toast.error('Name and slug are required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        qualifyCount: Number(form.qualifyCount) || 9,
        playoffEndRank: form.playoffEndRank === '' ? null : Number(form.playoffEndRank),
        scoring: {
          basePerHour: Number(form.scoring.basePerHour) || 50,
          games: form.scoring.games,
          timeMultipliers: form.scoring.timeMultipliers.map((t) => ({
            startHour: Number(t.startHour), endHour: Number(t.endHour), multiplier: Number(t.multiplier),
          })),
        },
      };
      if (editing) await api.put(`/admin/leaderboards/${editing.id}`, payload);
      else await api.post('/admin/leaderboards', payload);
      toast.success('Saved');
      setShowModal(false);
      fetchList();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const activate = async (lb) => {
    try { await api.post(`/admin/leaderboards/${lb.id}/activate`); toast.success(`"${lb.name}" is now live`); fetchList(); }
    catch (e) { toast.error('Failed to activate'); }
  };
  const remove = async (lb) => {
    if (!confirm(`Delete "${lb.name}"?`)) return;
    try { await api.delete(`/admin/leaderboards/${lb.id}`); toast.success('Deleted'); fetchList(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const setScoring = (patch) => setForm({ ...form, scoring: { ...form.scoring, ...patch } });
  const toggleGame = (g) => {
    const has = form.scoring.games.includes(g);
    setScoring({ games: has ? form.scoring.games.filter((x) => x !== g) : [...form.scoring.games, g] });
  };
  const setTier = (i, key, val) => {
    const tm = form.scoring.timeMultipliers.map((t, idx) => idx === i ? { ...t, [key]: val } : t);
    setScoring({ timeMultipliers: tm });
  };
  const addTier = () => setScoring({ timeMultipliers: [...form.scoring.timeMultipliers, { startHour: 0, endHour: 24, multiplier: 1 }] });
  const removeTier = (i) => setScoring({ timeMultipliers: form.scoring.timeMultipliers.filter((_, idx) => idx !== i) });

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Trophy className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">Leaderboards</h1>
        </div>
        {view === 'manage' && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-amber-500 text-black font-semibold px-4 py-2 rounded-lg hover:bg-amber-400">
            <Plus size={18} /> New Leaderboard
          </button>
        )}
      </div>

      {/* Sub-tabs: config manager + leaderboard reports */}
      <div className="flex gap-2 mb-5 border-b border-gray-700">
        {[
          { id: 'manage', label: 'Manage', icon: Settings },
          { id: 'wsop', label: 'Road to WSOP', icon: Trophy },
          { id: 'wpt', label: 'WPT Monthly', icon: Crown },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
              view === t.id ? 'border-amber-500 text-amber-400' : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {view === 'manage' && (
        <>
      <p className="text-sm text-gray-400 mb-4">The <span className="text-amber-400">active</span> leaderboard drives the app banner, the leaderboard screen, and scoring. If none is active, the app falls back to the built-in promo.</p>

      <div className="bg-gray-800 rounded-xl overflow-hidden">
        {loading ? <div className="p-10 text-center text-gray-400">Loading…</div>
        : list.length === 0 ? <div className="p-10 text-center text-gray-400">No leaderboards yet.</div>
        : (
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Window</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Scoring</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Active</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {list.map((lb) => (
                <tr key={lb.id} className="hover:bg-gray-750">
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{lb.name}</div>
                    <div className="text-xs text-gray-500">{lb.subtitle}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">{lb.startDate || '—'} → {lb.endDate || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">{lb.scoring?.basePerHour ?? 50}/hr · {(lb.scoring?.games || []).join(', ')} · top {lb.qualifyCount}</td>
                  <td className="px-4 py-3">
                    {lb.isActive
                      ? <span className="flex items-center gap-1 text-green-400 text-sm"><CheckCircle size={16} /> Live</span>
                      : <button onClick={() => activate(lb)} className="flex items-center gap-1 text-gray-400 hover:text-amber-400 text-sm"><Circle size={16} /> Activate</button>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(lb)} className="p-2 text-amber-500 hover:bg-amber-500/20 rounded-lg"><Pencil size={16} /></button>
                      <button onClick={() => remove(lb)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
        </>
      )}

      {view === 'wsop' && <WsopLeaderboardReport data={reportData} loading={reportLoading} />}
      {view === 'wpt' && <WptMonthlyReport data={reportData} loading={reportLoading} />}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-white">{editing ? 'Edit Leaderboard' : 'New Leaderboard'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X size={22} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <F label="Display name *"><input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="WPT World Championship Seat Qualifier" /></F>
                <F label="Slug *"><input className={inp} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="wpt-seat-qualifier" /></F>
              </div>
              <F label="Subtitle (banner)"><input className={inp} value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Top 9 qualify · Poker hours only" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Start date"><input className={inp} type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></F>
                <F label="End date"><input className={inp} type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></F>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <F label="Qualify (top N)"><input className={inp} type="number" value={form.qualifyCount} onChange={(e) => setForm({ ...form, qualifyCount: e.target.value })} /></F>
                <F label="Playoff end rank"><input className={inp} type="number" value={form.playoffEndRank} onChange={(e) => setForm({ ...form, playoffEndRank: e.target.value })} placeholder="optional" /></F>
                <F label="Base XP / hour"><input className={inp} type="number" value={form.scoring.basePerHour} onChange={(e) => setScoring({ basePerHour: e.target.value })} /></F>
              </div>

              <F label="Games counted">
                <div className="flex gap-2">
                  {GAMES.map((g) => (
                    <button key={g} onClick={() => toggleGame(g)} className={`px-3 py-1.5 rounded text-sm ${form.scoring.games.includes(g) ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'}`}>{g === 'cal_games' ? 'Cal Games' : 'Poker'}</button>
                  ))}
                </div>
              </F>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-400">Time-of-day multipliers (Pacific hours, 0–24)</label>
                  <button onClick={addTier} className="text-xs text-amber-400">+ Add</button>
                </div>
                {form.scoring.timeMultipliers.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <input className={`${inp} w-20`} type="number" value={t.startHour} onChange={(e) => setTier(i, 'startHour', e.target.value)} placeholder="from" />
                    <span className="text-gray-500">–</span>
                    <input className={`${inp} w-20`} type="number" value={t.endHour} onChange={(e) => setTier(i, 'endHour', e.target.value)} placeholder="to" />
                    <span className="text-gray-500 text-sm">×</span>
                    <input className={`${inp} w-20`} type="number" value={t.multiplier} onChange={(e) => setTier(i, 'multiplier', e.target.value)} placeholder="mult" />
                    <button onClick={() => removeTier(i)} className="text-red-400 p-1"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4 rounded" /> Enabled
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 px-4 py-2.5 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50">{saving ? 'Saving…' : editing ? 'Save' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = 'w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500';
function F({ label, children }) {
  return <div><label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>{children}</div>;
}
